import * as React from 'react';

import { useQueryClient, MutationStatus } from '@tanstack/react-query';

import useGetDispatchEventReport from '../api/hooks/dispatch-event/useGetDispatchEventReport';
import useUpdateDispatchEvent from '../api/hooks/dispatch-event/useUpdateDispatchEvent';
import useSubmitDispatchEvent from '../api/hooks/dispatch-event/useSubmitDispatchEvent';
import useApproveDispatchEvent, { IDispatchApprovalRequest } from '../api/hooks/dispatch-event/useApproveDispatchEvent';
import useGetAllowedActions from '../api/hooks/common/useGetAllowedActions';
import queryKeys from '../api/queryKeys';

import {
  AllowedActions,
  AuthorizationRequestType,
  DispatchEventReport,
  DispatchEventReportDTO,
  ReportType,
} from '../api/clients';
import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorOverlay from '../../components/ErrorOverlay';
import useTitle from '../api/hooks/common/useTitle';

type IDispatchEventReportData = {
  dispatchEventReport: DispatchEventReport;
  allowedActions: AllowedActions | undefined;

  update: (
    data: DispatchEventReport,
    successCallback?: any,
    failCallback?: any
  ) => void;
  updateStatus: MutationStatus;

  submit: (
    eventId: number | undefined,
    successCallback?: any,
    failCallback?: any,
  ) => void;
  submitStatus: MutationStatus;

  approve: (
    approvalRequest: IDispatchApprovalRequest,
    successCallback?: any,
    failCallback?: any,
  ) => void;
  approveStatus: MutationStatus;
};

const DispatchEventReportDataContext = React.createContext<IDispatchEventReportData>(undefined!);

export function DispatchEventReportDataProvider({
  children,
  dispatchEventReportId,
  role,
}: {
  children: React.ReactNode,
  dispatchEventReportId: number,
  role?: AuthorizationRequestType,
}) {
  const queryClient = useQueryClient();
  useTitle(document.documentURI);
  const {
    data: reportData,
    status: reportStatus,
    error: reportError,
  } = useGetDispatchEventReport(dispatchEventReportId, role || AuthorizationRequestType.Contribute);

  const { data: allowedActions } = useGetAllowedActions(
    reportData?.employeeId,
    reportData?.status,
    ReportType.DispatchEventReport,
  );

  const { status: updateMutationStatus, mutate: updateMutation } = useUpdateDispatchEvent();
  const { status: approveMutationStatus, mutate: approveMutation } = useApproveDispatchEvent();
  const { status: submitMutationStatus, mutate: submitMutation } = useSubmitDispatchEvent();

  const onSuccessfulMutation = (
    returnData: DispatchEventReportDTO,
    eventId: number | undefined,
    successCallback?: any,
    failCallback?: any,
  ) => {
    const { eventReport, error } = returnData;

    queryClient.setQueryData([queryKeys.DispatchEvent.Get, { id: eventId }], eventReport);
    if (!error) {
      if (successCallback) successCallback(eventReport);
    } else if (failCallback) failCallback(error);
  };

  const dispatchEventReportData = React.useMemo<IDispatchEventReportData>(() => ({
    dispatchEventReport: reportData!,
    allowedActions: allowedActions!,

    updateStatus: updateMutationStatus,
    update: (data: DispatchEventReport, successCallback?: any, failCallback?: any) => {
      updateMutation(data, {
        onSuccess: (returnData) => onSuccessfulMutation(
          returnData,
          data.id,
          successCallback,
          failCallback,
        ),
      });
    },

    approveStatus: approveMutationStatus,
    approve: (approvalRequest: IDispatchApprovalRequest, successCallback?: any, failCallback?: any) => {
      if (approvalRequest.reportId !== undefined) {
        approveMutation(approvalRequest, {
          onSuccess: (returnData) => onSuccessfulMutation(
            returnData,
            approvalRequest.reportId,
            successCallback,
            failCallback,
          ),
        });
      }
    },

    submitStatus: submitMutationStatus,
    submit: (eventId: number | undefined, successCallback?: any, failCallback?: any) => {
      if (eventId !== undefined) {
        submitMutation(eventId, {
          onSuccess: (returnData) => onSuccessfulMutation(
            returnData,
            eventId,
            successCallback,
            failCallback,
          ),
        });
      }
    },
  }), [
    reportData,
    allowedActions,
    updateMutationStatus,
    approveMutationStatus,
    submitMutationStatus,
  ]);

  if (reportStatus === 'pending') {
    return (
      <LoadingOverlay />
    );
  }

  if (reportStatus === 'error') {
    return (
      <ErrorOverlay
        title={reportError?.title}
        message={`An error occurred retrieving data for form: ${dispatchEventReportId}`}
      />
    );
  }

  if (reportStatus === 'success' && reportData !== undefined) {
    return (
      <DispatchEventReportDataContext.Provider value={dispatchEventReportData}>
        {children}
      </DispatchEventReportDataContext.Provider>
    );
  }

  return null;
}

export const useDispatchEventReportData = () => React.useContext(DispatchEventReportDataContext);
